import { useEffect, useState } from 'react'
import { Info } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { DefaultInfoModal } from '@/features/reimbursements'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import modalStyles from '@/components/ui/claimsModals.module.css'
import { residentConfirmPresentation } from '../../constants/residentDialogProps'
import { fetchRentalList, updateResidentsStatus } from '../../services/residentApi'
import type { RentalListResponse } from '../../types/resident'
import type { RentalStatusDialogProps } from '../../types'
import type { DefaultInfoModalType } from '@/features/reimbursements/types'

export function RentalStatusDialog({ isOpen, selectedApplicantIds, onClose, onUpdated }: RentalStatusDialogProps) {
  const [rentalList, setRentalList] = useState<RentalListResponse | null>(null)
  const [rentalStatus, setRentalStatus] = useState('')
  const [rentalProgress, setRentalProgress] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [infoModalState, setInfoModalState] = useState<{
    isOpen: boolean
    type: DefaultInfoModalType
  }>({
    isOpen: false,
    type: 'default_status',
  })

  useEffect(() => {
    if (!isOpen || rentalList) return
    void fetchRentalList().then(setRentalList).catch(showApiError)
  }, [isOpen, rentalList])

  const progressOptions = rentalStatus ? rentalList?.rental_status?.[rentalStatus]?.progress || {} : {}

  const submit = async () => {
    if (!rentalStatus || !rentalProgress) return
    setProcessing(true)
    try {
      await updateResidentsStatus({
        rental_status: rentalStatus,
        rental_progress: rentalProgress,
        page: 'resident',
        applicant_ids: selectedApplicantIds,
      })
      showSuccess('Status Updated Successfully')
      onUpdated()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setProcessing(false)
    }
  }

  return (
    <>
      <OnScreenModal
        isOpen={isOpen}
        title="Update Rental Status"
        onClose={onClose}
        maxWidth="max-w-2xl"
        bodyScrollOnly
        overlayDataTestId="rental-status-dialog-overlay"
        panelDataTestId="rental-status-dialog"
        closeButtonDataTestId="rental-status-dialog-close-button"
        bodyClassName="p-4 sm:p-5"
        footer={
          <div
            className={modalStyles.modalFooterActionGroup}
            data-testid="rental-status-dialog-footer"
          >
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={!rentalStatus || !rentalProgress || processing}
              data-testid="rental-status-dialog-submit-button"
              className={modalStyles.modalActionPrimary}
            >
              Submit
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              data-testid="rental-status-dialog-cancel-button"
              className={modalStyles.modalActionSecondary}
            >
              Cancel
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-[#232946]">
            <span className="flex items-center gap-1.5">
              <span>Rental Status</span>
              <button
                type="button"
                onClick={() => setInfoModalState({ isOpen: true, type: 'default_status' })}
                className="inline-flex items-center text-slate-400 hover:text-blue-600 focus:outline-none"
                title="Default Instructions"
                aria-label="Default Instructions"
                data-testid="rental-status-dialog-status-info-button"
              >
                <Info className="h-4 w-4" />
              </button>
            </span>
            <select
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
              value={rentalStatus}
              onChange={(event) => {
                setRentalStatus(event.target.value)
                setRentalProgress('')
              }}
              required
              data-testid="rental-status-dialog-status-select"
            >
              <option value="">Rental Status</option>
              {Object.entries(rentalList?.rental_status || {}).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.name || key}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-[#232946]">
            <span className="flex items-center gap-1.5">
              <span>Progress</span>
              <button
                type="button"
                onClick={() => setInfoModalState({ isOpen: true, type: 'default_document' })}
                className="inline-flex items-center text-slate-400 hover:text-blue-600 focus:outline-none"
                title="Required Documentation"
                aria-label="Required Documentation"
                data-testid="rental-status-dialog-progress-info-button"
              >
                <Info className="h-4 w-4" />
              </button>
            </span>
            <select
              className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20"
              value={rentalProgress}
              onChange={(event) => setRentalProgress(event.target.value)}
              required
              data-testid="rental-status-dialog-progress-select"
            >
              <option value="">Select Progress</option>
              {Object.entries(progressOptions).map(([key, value]) => (
                <option key={key} value={key} disabled={key === 'AWAITING_NEW_RESIDENT'}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        </div>
      </OnScreenModal>
      <DefaultInfoModal
        isOpen={infoModalState.isOpen}
        onClose={() => setInfoModalState((prev) => ({ ...prev, isOpen: false }))}
        type={infoModalState.type}
        status={rentalStatus}
      />
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Update Rental Status"
        message="Please confirm that you really want to update the rental status of the selected residents."
        primaryLabel="Update"
        secondaryLabel="Cancel"
        onPrimary={() => {
          setConfirmOpen(false)
          void submit()
        }}
        onSecondary={() => setConfirmOpen(false)}
        onDismiss={() => setConfirmOpen(false)}
        {...residentConfirmPresentation}
        overlayDataTestId="rental-status-confirm-overlay"
        primaryButtonDataTestId="rental-status-confirm-update-button"
        secondaryButtonDataTestId="rental-status-confirm-cancel-button"
        closeButtonDataTestId="rental-status-confirm-close-button"
      />
    </>
  )
}
